import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { createSubmit } from "../utils/createSubmit";
import { HASH_ALGORITHM_SHA3_256 } from "../utils/cryptoUtils";
import { formatHexStr } from "../utils/formatHexStr";
import { Box, Flex, Modal, Text, useMantineTheme } from "@mantine/core";
import CustomButton from "./mantineCustomComponents/CustomButton";
import CustomAnchor from "./mantineCustomComponents/CustomAnchor";
import { useStylesModalCreate } from "../utils/mantine/stampTabContent/useStylesModalCreate";
import {
  calculatePortfolioMetrics,
  getPortfolioWarnings,
} from "../utils/portfolioValidation";
const SelectPopUpCollection = ({
  onSelectPopUpShow,
  selectedOptionCollection,
  objectHash,
  selectedFile,
  setLoading,
  setError,
  handleVerifyHashWorker,
  handleSelectedOptionCollection,
  verificationMethod,
  modalOpened,
  setFileName,
  normalizedPortfolio,
  setIsAuthenticated,
}) => {
  // Mantine theme instance to apply custom styles and theming throughout the component
  const theme = useMantineTheme();

  //css classes
  const { classes: classesModalCreate } = useStylesModalCreate();

  // Modal Stamp
  const [openedModal, setOpenedModal] = useState(modalOpened);

  const closeModal = () => {
    setOpenedModal(false);
    onSelectPopUpShow(false);
  };

  const handleConfirmCollection = () => {
    // Close modal first
    onSelectPopUpShow(false);
    // Set loading immediately after closing modal (use setTimeout to ensure modal closes first)
    setTimeout(() => {
      setLoading(true);
    }, 0);
    // Then start the submit process
    createSubmit(
      objectHash,
      selectedFile,
      setLoading,
      setError,
      handleVerifyHashWorker,
      selectedOptionCollection,
      handleSelectedOptionCollection,
      verificationMethod,
      setFileName,
      setIsAuthenticated,
    );
  };
  const handleClosePopUpCollection = () => {
    onSelectPopUpShow(false);
  };

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Function to update `isMobile` state based on screen width
    const updateScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Add event listener for screen resizing
    window.addEventListener("resize", updateScreenSize);

    // Set the initial value for `isMobile`
    updateScreenSize();

    // Clean up event listener on component unmount
    return () => window.removeEventListener("resize", updateScreenSize);
  }, []);
  return (
    <Modal
      className={classesModalCreate.modal}
      opened={openedModal}
      onClose={closeModal}
      title={
        verificationMethod === "portfolio"
          ? "Confirm Portfolio Stamp"
          : "Confirm Stamp Creation"
      }
      size="auto"
      centered
    >
      <Box
        className={`${classesModalCreate.modalText} confirmation-collection__text`}
      >
        <Text component="b">Collection Name:</Text>{" "}
        {selectedOptionCollection?.name
          ? selectedOptionCollection.name
          : "<<None>>"}
      </Box>

      {verificationMethod !== "portfolio" && (
        <Box
          className={`${classesModalCreate.modalText} confirmation-collection__text`}
        >
          {isMobile ? (
            <>
              <Text component="b">Content ID ({HASH_ALGORITHM_SHA3_256}):</Text>{" "}
              {formatHexStr(objectHash)}
            </>
          ) : (
            <>
              <Text
                component="b"
                className={classesModalCreate.modalTextCollectionKey}
              >
                Content ID ({HASH_ALGORITHM_SHA3_256}):
              </Text>{" "}
              {objectHash}
            </>
          )}
        </Box>
      )}

      {verificationMethod === "portfolio" &&
        normalizedPortfolio &&
        (() => {
          // Calculate metrics once and reuse to avoid redundant processing
          const metrics = calculatePortfolioMetrics(normalizedPortfolio);
          const warnings = getPortfolioWarnings(metrics);

          return (
            <>
              <Box className={classesModalCreate.portfolioSection}>
                <Text className={classesModalCreate.portfolioMetricsTitle}>
                  Portfolio Metrics:
                </Text>
                <Box>
                  <Text className={classesModalCreate.portfolioMetricsValue}>
                    <Text
                      component="b"
                      className={classesModalCreate.portfolioMetricsLabel}
                    >
                      Gross Exposure:
                    </Text>{" "}
                    {metrics.grossWeights.toFixed(4)}
                  </Text>
                  <Text className={classesModalCreate.portfolioMetricsValue}>
                    <Text
                      component="b"
                      className={classesModalCreate.portfolioMetricsLabel}
                    >
                      Net Exposure:
                    </Text>{" "}
                    {metrics.netWeights.toFixed(4)}
                  </Text>
                  <Text className={classesModalCreate.portfolioMetricsValue}>
                    <Text
                      component="b"
                      className={classesModalCreate.portfolioMetricsLabel}
                    >
                      Symbols:
                    </Text>{" "}
                    {metrics.totalSymbols}
                  </Text>
                </Box>
              </Box>

              {warnings.length > 0 && (
                <Box className={classesModalCreate.portfolioSection}>
                  {warnings.map((warning, idx) => (
                    <Text
                      key={idx}
                      className={classesModalCreate.portfolioWarning}
                    >
                      {warning}
                    </Text>
                  ))}
                </Box>
              )}

              <Box className={classesModalCreate.portfolioSection}>
                <Text className={classesModalCreate.portfolioMetricsTitle}>
                  Portfolio Preview
                </Text>
                <Box
                  component="pre"
                  className={classesModalCreate.portfolioPreview}
                >
                  {normalizedPortfolio}
                </Box>
              </Box>
            </>
          );
        })()}

      {verificationMethod === "portfolio" && (
        <Box
          className={`${classesModalCreate.modalText} ${classesModalCreate.portfolioCid}`}
        >
          {isMobile ? (
            <>
              <Text component="b">Content ID:</Text> {formatHexStr(objectHash)}
            </>
          ) : (
            <>
              <Text component="b">Content ID:</Text> {objectHash}
            </>
          )}

          <Text className={classesModalCreate.portfolioNormalizedNote}>
            Portfolio fingerprint (Content ID) is stamped. Portfolio CSV
            available via confirmation screen and{" "}
            <CustomAnchor
              text="Stamped File Storage"
              href="/profile/#user_data_storage"
              componentClasses="link"
            />
            .
          </Text>
        </Box>
      )}

      <Box className={classesModalCreate.modalConfirmationTitle}>Proceed?</Box>
      <Flex
        justify={theme.flex.justify.center}
        gap={theme.flex.gap.large}
        mb={theme.margin.xl}
      >
        <CustomButton
          text="Yes"
          componentClasses="buttonBlack confirm-stamp-create"
          onClick={() => handleConfirmCollection()}
        ></CustomButton>
        <CustomButton
          text="No"
          componentClasses="buttonWhite"
          onClick={() => handleClosePopUpCollection()}
        ></CustomButton>
      </Flex>
    </Modal>
  );
};

SelectPopUpCollection.propTypes = {
  selectedOptionCollection: PropTypes.object.isRequired,
  onSelectPopUpShow: PropTypes.func.isRequired,
  objectHash: PropTypes.string.isRequired,
  verificationMethod: PropTypes.string.isRequired,
  selectedFile: PropTypes.object.isRequired,
  setLoading: PropTypes.func.isRequired,
  setError: PropTypes.func.isRequired,
  handleVerifyHashWorker: PropTypes.func.isRequired,
  handleSelectedOptionCollection: PropTypes.func.isRequired,
  modalOpened: PropTypes.bool,
  setFileName: PropTypes.func.isRequired,
  normalizedPortfolio: PropTypes.string,
  setIsAuthenticated: PropTypes.func,
};

export default SelectPopUpCollection;
